import React, { useEffect, useState } from 'react';
import { BsArrowLeftShort, BsArrowRightShort } from 'react-icons/bs';
import { useHistory } from 'react-router-dom';

import { saveOnBoardData } from 'api/onBoarding';
import InputField from 'components/FormFields/InputField';
import LocationSelect from 'components/LocationSelect';
import APFormGroup from 'components/FormFields/APFormGroup';
import { prepareLocationForApi } from 'utils/location';
import { isUrlValid } from 'utils/general';
import { getPutFormat } from './filter';
import './styles.scss';

// Converts the address model to google place like object.
const formatAddress = (address) => {
  const { city, country, postcode, state, street_line_1 } = address;
  if ((city === '' && state === '') || (city === ' ' && state === ' ')) {
    return null;
  }
  let formattedAddress = '';
  if (street_line_1) formattedAddress += `${street_line_1}, `;
  if (city) formattedAddress += `${city}, `;
  if (state) formattedAddress += `${state} `;
  if (postcode) formattedAddress += `${postcode}`;
  if (country) formattedAddress += `, ${country}`;
  return { ...address, formattedAddress };
};

const getFormattedNumber = (phoneNumber) => {
  const numberDigits = phoneNumber.replace(/ /g, '');
  if (numberDigits && numberDigits.length === 11 && numberDigits[0] === '1') {
    return numberDigits
      .substring(1)
      .replace(/(\d{3})(\d{3})(\d{4})/, '1 ($1) $2-$3');
  }
  if (numberDigits && numberDigits.length === 10) {
    return numberDigits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  }
  return phoneNumber;
};

const Step2 = ({ formData, setLocation }) => {
  const history = useHistory();
  const [isSubmitted, setFormSubmitted] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [businessAddress, setBusinessAddress] = useState();
  const [phoneNumber, setPhoneNumber] = useState('');
  const [website, setWebsite] = useState('');

  useEffect(() => {
    const { companyInfo } = formData;
    if (companyInfo) {
      const { businessAddress: address, companyName: cName } = companyInfo;
      setCompanyName(cName || '');
      const updatedAddress = formatAddress(address);
      setBusinessAddress(updatedAddress);
      setPhoneNumber(companyInfo.phoneNumber || '');
      setWebsite(companyInfo.website || '');
    }
  }, [formData]);

  const isWebsiteFieldValid = () => {
    // Pass if website is empty. We can't validate empty url.
    if (!website) return true;
    return website && isUrlValid(website);
  };

  const isFormFieldsValid = () => {
    return !!companyName && !!businessAddress && !!phoneNumber;
  };

  const saveForms = async () => {
    const newFormData = formData;
    newFormData.companyInfo.companyName = companyName;
    if (businessAddress) {
      newFormData.companyInfo.businessAddress = prepareLocationForApi(businessAddress);
    } else {
      newFormData.companyInfo.businessAddress = {};
    }

    newFormData.companyInfo.phoneNumber = phoneNumber;
    newFormData.companyInfo.website = website;

    const result = await saveOnBoardData({ boarding: getPutFormat(newFormData) });
    return result;
  };

  const handleBack = () => {
    history.push('/app/v2/account-setup/step/1');
  };

  const handleNext = async () => {
    setLocation(businessAddress);
    setFormSubmitted(true);
    if (isFormFieldsValid() && isWebsiteFieldValid()) {
      const response = await saveForms();
      if (response) history.push('/app/v2/account-setup/step/3');
    }
  };

  return (
    <>
      <section className="stepflow_content">
        <article className="stepflow_form">
          <section className="stepflow_form-header">
            <h3 className="form-title">Company profile</h3>
            <p className="form-subtitle text-center">
              We'll use this information to populate everything from your
              company settings to your quote and invoice templates. Our promise
              to you—we won't sell, trade or abuse any information you submit to
              us.
            </p>
          </section>
          <APFormGroup
            verticalGap={0}
          >
            <InputField
              name="companyName"
              label="Company Name"
              value={companyName}
              required
              onChange={(e) => {
                setCompanyName(e.target.value);
              }}
              showError={isSubmitted && !companyName}
            />
          </APFormGroup>
          <APFormGroup
            verticalGap={0}
          >
            <LocationSelect
              location={businessAddress}
              setLocation={setBusinessAddress}
              name="Business Address"
              placeholder="Please enter your street address and choose from the options below."
              required
              error={isSubmitted && !businessAddress}
            />
          </APFormGroup>
          <APFormGroup
            verticalGap={0}
            horizontalGap={1}
          >
            <InputField
              name="phoneNumber"
              label="Phone number"
              value={phoneNumber}
              required
              onChange={(e) => {
                const phone = e.target.value;
                setPhoneNumber(phone);
              }}
              onBlur={() => {
                const newPhoneNumber = getFormattedNumber(phoneNumber);
                setPhoneNumber(newPhoneNumber);
              }}
              showError={isSubmitted && !phoneNumber}
            />
            <InputField
              name="website"
              label="Website"
              value={website}
              onChange={(e) => {
                setWebsite(e.target.value);
              }}
              onBlur={() => {
                if (
                  website &&
                  !website.includes('https://') &&
                  !website.includes('http://')
                ) {
                  setWebsite(`http://${website}`);
                }
              }}
              showError={isSubmitted && !isWebsiteFieldValid()}
            />
          </APFormGroup>
        </article>
      </section>
      <section className="stepflow_footer">
        <button className="stepflow_footer-button"
          onClick={handleBack}
        >
          <BsArrowLeftShort size={20} /> Back
        </button>
        {isSubmitted && !isFormFieldsValid() && (
          <div className="stepflow_footer-error">
            Please complete required fields above.
          </div>
        )}
        {isSubmitted && isFormFieldsValid() && !isWebsiteFieldValid() && (
          <div className="stepflow_footer-error">
            Please review and update your website format to continue.
          </div>
        )}
        <button
          className="stepflow_footer-button"
          color="primary"
          onClick={handleNext}
        >
          Next
          <BsArrowRightShort size={20} />
        </button>
      </section>
    </>
  );
};

export default Step2;
